import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'

/**
 * Landing page for admin impersonation.
 *
 * Receives a one-time exchange code via `?c=<code>` query param, POSTs
 * it to `/api/auth/impersonate/exchange` to atomically swap it for a
 * session JWT, then scrubs the URL with history.replaceState before
 * routing to the dashboard.
 *
 * The legacy `?token=<JWT>` URL has been retired — the admin portal
 * only emits `?c=` links once this deploys.
 */
export function ImpersonatePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const startImpersonation = useAuthStore((s) => s.startImpersonation)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const code = searchParams.get('c')
    if (!code) {
      setError('No impersonation code provided.')
      return
    }

    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

    fetch(`${apiBase}/auth/impersonate/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.message || 'Impersonation code is invalid or expired.')
        }
        return res.json()
      })
      .then((body) => {
        const data = body?.data
        if (!data?.token || !data?.user || !data?.impersonatedBy) {
          throw new Error('Malformed impersonation response.')
        }

        const user = {
          ...data.user,
          firstName: data.user.firstName ?? '',
          lastName: data.user.lastName ?? '',
          displayName: data.user.displayName ?? data.user.email,
          role: data.user.role || 'ESTIMATOR',
          status: 'active' as const,
          createdAt: new Date().toISOString(),
        }

        startImpersonation(user, data.token, data.impersonatedBy)

        // Scrub the code out of the URL before any tracking/replay tooling
        // can capture it.
        try {
          window.history.replaceState(null, '', '/dashboard')
        } catch {
          // Fall through to navigate() — best-effort scrub.
        }

        navigate('/dashboard', { replace: true })
      })
      .catch((err) => {
        console.error('Failed to start impersonation:', err)
        setError(err?.message || 'Failed to start impersonation session.')
      })
  }, [searchParams, navigate, startImpersonation])

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'system-ui, sans-serif',
        color: '#64748B',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 style={{ color: '#1E293B', fontSize: 18, marginBottom: 8 }}>Impersonation Failed</h2>
          <p>{error}</p>
          <p style={{ marginTop: 16 }}>
            <a href="/login" style={{ color: '#3B82F6' }}>Go to login</a>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'system-ui, sans-serif',
      color: '#64748B',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div className="adm-spinner" style={{ margin: '0 auto 16px', width: 32, height: 32, border: '3px solid #E2E8F0', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        <p>Starting impersonation session...</p>
      </div>
    </div>
  )
}
